'use client';

import { c as _c } from "react/compiler-runtime";
import type { RefCallback } from 'react';
import { useEffect, useRef } from 'react';
interface UseInfiniteScrollSentinelOptions {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** When true, suppress auto-fetch so a persistent page error doesn't retry-loop. */
  isError?: boolean;
  /** Extra dependency to re-observe when pages change. */
  pageCount?: number;
}

/**
 * Returns a ref callback for a sentinel div. When the sentinel enters the viewport,
 * triggers fetchNextPage. Uses IntersectionObserver with 200px rootMargin.
 */
export function useInfiniteScrollSentinel(options) {
  const $ = _c(7);
  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isError: t0
  } = options;
  const isError = t0 === undefined ? false : t0;
  const sentinelRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = node => {
      sentinelRef.current = node;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const callbackRef = t1;
  let t2;
  let t3;
  if ($[1] !== fetchNextPage || $[2] !== hasNextPage || $[3] !== isError || $[4] !== isFetchingNextPage) {
    t2 = () => {
      const node_0 = sentinelRef.current;
      if (node_0 === null) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage && !isError) {
          fetchNextPage();
        }
      }, {
        rootMargin: "200px"
      });
      observer.observe(node_0);
      return () => {
        observer.disconnect();
      };
    };
    t3 = [hasNextPage, isFetchingNextPage, fetchNextPage, isError];
    $[1] = fetchNextPage;
    $[2] = hasNextPage;
    $[3] = isError;
    $[4] = isFetchingNextPage;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  return callbackRef;
}